import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import type { Comic } from '@tanko/shared'
import { db, type LocalUploadSession } from '../db/schema'
import { bindCloudComic, clearUploadSession, saveUploadSession } from '../db/repo'
import { localReadingStore } from '../db/reading-repo'
import { CloudApiClient, CloudApiError, type UploadSessionState } from '../lib/api-client'
import { errorMessage, imageFileNames } from '../lib/fs-access'
import { resolveComicArchive, resolveComicDirectory } from '../lib/library-sources'
import { ArchiveSource } from '../sources/archive-source'
import { PageConverter } from '../lib/page-converter'
import { createThumbnail } from '../lib/thumbnail'
import { compareUploadSource, createPublicationSnapshot, pendingUploadParts, uploadReadiness, type SourceFileSnapshot } from '../lib/upload-session'
import { registerComics } from '../lib/catalog-registration'
import { runBounded } from '../lib/bulk-progress'
import { isRegistered } from '../lib/comic-sync'
import { isExpectedFailure, stepError } from '../lib/step-error'
import { backupFailureKind, SourceUnauthorizedError, type BackupFailureKind } from '../lib/backup-failure'
import { useLibraryStore } from './library'

export type UploadPhase = 'idle' | 'loading' | 'ready' | 'converting' | 'verifying' | 'publishing' | 'binding' | 'complete' | 'failed' | 'cancelling' | 'cancelled'
const api = new CloudApiClient()

/**
 * 同時處理幾頁。
 *
 * 壓縮檔的每一頁都要真的解壓成 Blob 才能轉檔，因此一次開幾頁就有幾個 Blob 躺在記憶體裡。
 * 原本用 Promise.all 一次全開，一本 615 頁的壓縮檔會同時產生 615 個，把分頁吃垮——
 * 線上那本 615 頁的就死在第 259 頁。資料夾作品不受影響（File 是懶惰參照），但沒有理由
 * 分兩套。
 *
 * 不需要更大：轉檔限制在兩個 worker，再多的並行只會讓更多來源 Blob 提早進記憶體等著。
 */
const PAGE_CONCURRENCY = 4

/**
 * 意外錯誤附上步驟與錯誤型別。
 *
 * 上傳有五個各自捕捉錯誤的步驟，而它們都只把 error.message 放上畫面。遇到不是
 * 我們自己拋的錯誤（例如瀏覽器的 TypeError）時，畫面上只會出現像「Illegal
 * invocation」這種無從追查的字串，且因為已被捕捉，主控台也不會有任何東西。
 * 帶上步驟與型別之後，回報的訊息本身就足以定位。
 */
/**
 * 意外型別會被五個步驟各自捕捉，主控台因此什麼都不會有；這是唯一能拿到完整堆疊的
 * 地方，缺它就只能靠畫面上那句話猜。訊息本身的組裝在 lib/step-error.ts。
 */
function reportUnexpected(step: string, cause: unknown): void {
  if (!isExpectedFailure(cause)) console.error(`[tanko] ${step} 發生非預期的錯誤`, cause)
}

export const useUploadStore = defineStore('upload', () => {
  const library = useLibraryStore()
  const comic = shallowRef<Comic>()
  const phase = ref<UploadPhase>('idle')
  const error = ref('')
  // 佇列只讀這個分類決定要繼續下一本還是暫停整批，不解析 error 的字串。
  const failureKind = ref<BackupFailureKind>('none')
  const sourceChanged = ref(false)
  const restartRequired = ref(false)
  const completedParts = ref(0)
  const totalParts = ref(0)
  const server = shallowRef<UploadSessionState>()
  const hasSession = ref(false)
  const publicationLocked = ref(false)
  let local: LocalUploadSession | undefined
  let files: Blob[] = []
  // 壓縮檔作品在整個上傳期間保持開啟，逐頁解壓，不把整份內容載進記憶體。
  let archive: ArchiveSource | undefined
  let sourceManifest: SourceFileSnapshot[] = []
  /**
   * 即將發布的封面頁，在 prepare 解析一次之後由轉檔與發布共用。
   *
   * 分開解析是這條流程過去永久卡住的其中一個原因：轉檔階段取書庫 store 記憶體裡的
   * coverPage，發布階段取 IndexedDB 讀出來的值，剛換過封面時兩者不同，伺服器就以
   * 「缺少這一頁的封面縮圖」拒絕發布，而畫面上看起來像是上傳無緣無故失敗。
   */
  let publishCoverPage = 0

  async function pageBlob(page: number): Promise<Blob> {
    return archive ? archive.getPage(page) : files[page]
  }
  async function releaseSource() {
    const open = archive
    archive = undefined
    files = []
    await open?.dispose()
  }
  let controller: AbortController | undefined
  let converter: PageConverter | undefined

  const busy = computed(() => ['converting', 'verifying', 'publishing', 'binding', 'cancelling'].includes(phase.value))
  const progressLabel = computed(() => {
    if (phase.value === 'converting') return `已完成 ${completedParts.value}／${totalParts.value} 個圖片檔`
    if (phase.value === 'verifying') return `正在核對完整性${server.value ? ` · ${server.value.verificationCursor}／${server.value.pageCount} 頁` : ''}`
    if (phase.value === 'publishing') return '正在發布作品與閱讀資料…'
    if (phase.value === 'binding') return '作品已發布，正在寫回本機綁定…'
    return ''
  })

  /** 失敗一律經過這裡，讓訊息與分類不會有一邊漏設。 */
  function fail(step: string, cause: unknown) {
    reportUnexpected(step, cause)
    phase.value = 'failed'
    failureKind.value = backupFailureKind(cause)
    error.value = stepError(step, cause)
  }

  async function prepare(item: Comic) {
    if (busy.value && comic.value?.id !== item.id) {
      error.value = '目前有另一本作品正在上傳；你仍可關閉面板並繼續閱讀或整理書庫。'
      return
    }
    comic.value = item
    if (busy.value) return
    // 換一本作品時釋放上一本的壓縮檔來源，避免累積開啟的讀取器。
    await releaseSource()
    phase.value = 'loading'
    error.value = ''
    failureKind.value = 'none'
    sourceChanged.value = false
    restartRequired.value = false
    server.value = undefined
    hasSession.value = false
    publicationLocked.value = false
    local = undefined
    sourceManifest = []
    completedParts.value = 0
    totalParts.value = item.pageCount + 1
    publishCoverPage = item.coverPage
    try {
      // 發布是把既有的目錄項轉為已備份，因此上傳之前這一本必須先在雲端目錄裡。
      // 在這裡補註冊，而不是等到發布才發現——那時轉檔與上傳都已經白跑一趟。
      if (!isRegistered(item)) {
        const outcome = await registerComics([item], api)
        // 帶上原始例外，否則登入失效會被當成這一本自己的問題而讓佇列繼續撞同一道牆。
        if (outcome.reason) throw new Error(`無法在雲端目錄註冊「${item.title}」：${outcome.reason}`, { cause: outcome.cause })
      }
      // 到這裡為止不碰檔案系統：本機 session 紀錄與雲端 session 狀態都不需要來源。
      // 圖片早就傳完、只差一個發布呼叫的作品因此不必先通過來源授權與整本重讀。
      local = await db.uploadSessions.get(item.id)
      hasSession.value = !!local
      publicationLocked.value = !!local?.publication
      // 即將發布的封面頁：已固定的發布快照優先，否則取 IndexedDB 當下的紀錄。
      // 不用書庫 store 記憶體裡那一份——剛換過封面時它會落後，導致傳的封面頁與發布的不同頁。
      publishCoverPage = local?.publication?.coverPage ?? (await db.comics.get(item.id))?.coverPage ?? item.coverPage
      const state = await loadServerSession(local?.uploadId)
      server.value = state
      if (state) {
        if (state.status === 'cancelled' || state.status === 'expired') {
          restartRequired.value = true
          phase.value = 'failed'
          error.value = '這次上傳已取消或過期，請重新建立上傳。'
          return
        }
        const readiness = uploadReadiness(state, local!.pageCount, publishCoverPage)
        if (readiness.tier === 'bound') { await recoverBinding(readiness.cloudComicId); return }
        if (readiness.tier === 'publish-only') {
          // 所有分片都在伺服器上，剩下的工作一個位元組都不需要讀。
          completedParts.value = totalParts.value
          phase.value = 'ready'
          return
        }
        completedParts.value = totalParts.value - readiness.pending.pages.length - (readiness.pending.cover === null ? 0 : 1)
      }
      await openSource(item)
      // openSource 可能已把 phase 設為 failed（來源與 session 的快照不符），不要覆蓋。
      if (phase.value === 'loading') phase.value = 'ready'
    } catch (cause) {
      fail('準備上傳', cause)
    }
  }

  /** 取得伺服器上的 session 狀態；沒有本機紀錄或伺服器已無此 session 時回 undefined。 */
  async function loadServerSession(uploadId: string | undefined) {
    if (!uploadId) return undefined
    try { return await api.getUpload(uploadId) }
    catch (cause) {
      if (cause instanceof CloudApiError && cause.status === 404) return undefined
      throw cause
    }
  }

  /**
   * 開啟來源並比對快照。只有確定需要補傳分片時才走到這裡——授權提示、整本重讀與
   * 快照比對都只對真的要讀檔案的情況成立。
   */
  async function openSource(item: Comic) {
    const librarySource = library.sourceOf(item)
    if (!librarySource || !library.isAuthorized(librarySource.id)) throw new SourceUnauthorizedError('請先開啟這本作品的來源資料夾，才能讀取上傳來源。')
    const source = await loadSource(librarySource.handle, item)
    files = source.files
    archive = source.archive
    sourceManifest = source.manifest
    if (source.pageCount !== item.pageCount) throw new Error(`${item.kind === 'archive' ? '壓縮檔' : '本地資料夾'}目前有 ${source.pageCount} 頁，但書庫索引是 ${item.pageCount} 頁。請先重整書庫再上傳。`)
    if (!local) return
    const comparison = compareUploadSource(local.sourceManifest, sourceManifest)
    if (!comparison.changed) return
    // 只標記，不取消：伺服器上已完成的分片不因為本地檔案被搬動而作廢。
    // restartRequired 維持 false，讓「續傳」與「重新上傳」兩個出口同時可用。
    sourceChanged.value = true
    error.value = comparison.message
  }

  async function start() {
    const item = comic.value
    if (!item || busy.value || restartRequired.value) return
    error.value = ''
    failureKind.value = 'none'
    try {
      if (!local) {
        const now = Date.now()
        local = {
          comicId: item.id, uploadId: crypto.randomUUID(), pageCount: item.pageCount,
          sourceManifest, createdAt: now, updatedAt: now,
        }
        // uploadId 必須在第一個網路請求之前持久化。
        await saveUploadSession(local)
        hasSession.value = true
      }
      // 選擇續傳就採用目前這份快照，否則下一次開啟又會再判定一次不符。
      if (sourceChanged.value) {
        local = { ...local, sourceManifest, updatedAt: Date.now() }
        sourceChanged.value = false
        error.value = ''
      }
      await saveUploadSession(local)
      server.value = await api.createUpload(local.uploadId, local.pageCount)
      await runUpload()
    } catch (cause) {
      fail('建立上傳', cause)
    }
  }

  async function restart() {
    if (!comic.value || busy.value || !local) return
    phase.value = 'cancelling'
    error.value = ''
    failureKind.value = 'none'
    try {
      try { await api.cancelUpload(local.uploadId) }
      catch (cause) { if (!(cause instanceof CloudApiError && cause.status === 404)) throw cause }
      await clearUploadSession(local.comicId, local.uploadId)
      local = undefined
      hasSession.value = false
      publicationLocked.value = false
      server.value = undefined
      sourceChanged.value = false
      restartRequired.value = false
      phase.value = 'ready'
      await start()
    } catch (cause) {
      fail('重新建立上傳', cause)
    }
  }

  async function cancel() {
    if (!local || phase.value === 'publishing' || phase.value === 'binding') return
    phase.value = 'cancelling'
    error.value = ''
    controller?.abort()
    converter?.dispose()
    try {
      try { await api.cancelUpload(local.uploadId) }
      catch (cause) { if (!(cause instanceof CloudApiError && cause.status === 404)) throw cause }
      await clearUploadSession(local.comicId, local.uploadId)
      local = undefined
      hasSession.value = false
      publicationLocked.value = false
      server.value = undefined
      phase.value = 'cancelled'
    } catch (cause) {
      fail('取消上傳', cause)
    }
  }

  async function runUpload() {
    const item = comic.value
    if (!item || !local || !server.value) return
    controller = new AbortController()
    converter = new PageConverter()
    try {
      // 封面頁以將要發布的那個為準：發布資料一旦固定就用它，否則用作品目前的封面頁。
      const coverPage = publishCoverPage
      const pending = pendingUploadParts(local.pageCount, coverPage, server.value.received, server.value.lastErrorPage)
      totalParts.value = local.pageCount + 1
      completedParts.value = totalParts.value - pending.pages.length - (pending.cover === null ? 0 : 1)
      phase.value = 'converting'
      // 封面整本只有一張，用本地封面的同一個產生器（350px），雲端卡片畫質才與本地一致。
      if (pending.cover !== null) {
        const thumbnail = await createThumbnail(await pageBlob(pending.cover), controller.signal)
        await api.uploadPart(local.uploadId, pending.cover, 'cover', crypto.randomUUID(), thumbnail.blob, thumbnail.width, thumbnail.height, controller.signal)
        completedParts.value += 1
      }
      await runBounded(pending.pages, PAGE_CONCURRENCY, async page => {
        const converted = await converter!.convert(await pageBlob(page), controller!.signal)
        await api.uploadPart(local!.uploadId, page, 'image', crypto.randomUUID(), converted.image, converted.width, converted.height, controller!.signal)
        completedParts.value += 1
      })
      if (controller.signal.aborted) return
      phase.value = 'verifying'
      do {
        server.value = await api.verifyUpload(local.uploadId)
      } while (server.value.status === 'verifying')
      if (server.value.status !== 'verified') {
        throw new Error(server.value.lastErrorPage === null
          ? '完整性驗證未完成，請重試。'
          : `第 ${server.value.lastErrorPage + 1} 頁的圖片不完整，請重試該頁。`)
      }
      await publish()
    } catch (cause) {
      controller.abort()
      if (phase.value !== 'cancelling' && phase.value !== 'cancelled') fail('轉檔與上傳', cause)
    } finally {
      converter.dispose()
      converter = undefined
      controller = undefined
    }
  }

  async function publish() {
    const item = comic.value
    if (!item || !local) return
    if (!local.publication) {
      const current = await db.comics.get(item.id)
      if (!current) throw new Error('作品已被移除，無法發布。')
      const state = await localReadingStore(item.id, item.pageCount).load()
      local = {
        ...local,
        // 封面頁用 prepare 解析好的那一份，確保與實際傳上去的封面分片是同一頁。
        publication: createPublicationSnapshot(current, { ...state, coverPage: publishCoverPage }),
        updatedAt: Date.now(),
      }
      // 固定 publication mutationId，讓 response 丟失後能安全重送。
      await saveUploadSession(local)
      publicationLocked.value = true
    }
    const publication = local.publication
    if (!publication) throw new Error('找不到已固定的發布資料。')
    phase.value = 'publishing'
    let cloudComicId: string
    try {
      cloudComicId = (await api.publishUpload(local.uploadId, publication)).comicId
      if (server.value) server.value = { ...server.value, status: 'ready', comicId: cloudComicId }
    } catch (cause) {
      const recovered = await api.getUpload(local.uploadId).catch(() => undefined)
      if (!recovered?.comicId || recovered.status !== 'ready') throw cause
      server.value = recovered
      cloudComicId = recovered.comicId
    }
    phase.value = 'binding'
    await recoverBinding(cloudComicId)
  }

  async function recoverBinding(cloudComicId: string) {
    if (!comic.value || !local) return
    try {
      await bindCloudComic(comic.value.id, local.uploadId, cloudComicId)
      local = undefined
      hasSession.value = false
      publicationLocked.value = false
      phase.value = 'complete'
      error.value = ''
      failureKind.value = 'none'
    } catch (cause) {
      phase.value = 'failed'
      failureKind.value = backupFailureKind(cause)
      error.value = `作品已發布，但本機綁定尚未寫入。再次開啟面板即可復原。${errorMessage(cause)}`
    }
  }

  /**
   * 依作品形態取得頁面與來源快照。
   * 資料夾的 File 本身就是磁碟上的懶惰參照，先解析好不佔記憶體；
   * 壓縮檔則保持來源開啟，由 pageBlob 逐頁解壓。
   */
  async function loadSource(root: FileSystemDirectoryHandle, item: Comic) {
    if (item.kind === 'archive') {
      const file = await resolveComicArchive(root, item.relPath)
      const source = await ArchiveSource.create(file)
      const manifest: SourceFileSnapshot[] = source.entryNames.map(name => ({
        name, size: file.size, lastModified: file.lastModified,
      }))
      return { files: [] as Blob[], archive: source, manifest, pageCount: source.pageCount }
    }
    const directory = await resolveComicDirectory(root, item.relPath)
    const names = await imageFileNames(directory)
    const sourceFiles: Blob[] = []
    const manifest: SourceFileSnapshot[] = []
    for (const name of names) {
      const file = await (await directory.getFileHandle(name)).getFile()
      sourceFiles.push(file)
      manifest.push({ name, size: file.size, lastModified: file.lastModified })
    }
    return { files: sourceFiles, archive: undefined, manifest, pageCount: sourceFiles.length }
  }

  return {
    comic, phase, error, failureKind, sourceChanged, restartRequired,
    completedParts, totalParts, server, busy, hasSession, progressLabel,
    prepare, start, restart, cancel,
  }
})
